'use client';

import { getKeyValue } from '@/services/content/data';
import { SearchServiceProps } from '@/services/search/types';
import {
    CategoryFilterView,
    CategoryGRSFilterView,
    DefaultFilterView,
    LocationFilterView,
    PricingFilterView,
    ProgramFilterView,
    RatingFilterView,
} from '@/src/components/FilterGroup/views';
import {
    GRS_BOPIW_FACET_KEY,
    GRS_DELIVERY_FACET_KEY,
    GRS_RATING_FACET_KEY,
    GRS_SHOW_OUT_OF_STOCK_FACET_KEY,
    GRS_TWO_DAY_FACET_KEY,
    LW_BOPIM_FACET_KEY,
    LW_BOPIW_FACET_KEY,
    LW_BUY_IN_WAREHOUSE_FACET_KEY,
    LW_CATEGORY_FACET_KEY,
    LW_DELIVERY_FACET_KEY,
    LW_PRICE_FACET_KEY,
    LW_RATING_FACET_KEY,
    LW_SHOW_OUT_OF_STOCK_FACET_KEY,
    LW_TWO_DAY_FACET_KEY,
} from '@/src/components/SearchResultsFacets/constants';
import {
    GRS_BOPIM_FACET_KEY,
    GRS_BUY_IN_WAREHOUSE_FACET_KEY,
    GRS_CATEGORY_INFO_FACET_KEY,
} from '@/src/components/SearchResultsFacets/grs.constants';
import { isGRSPriceFacetKey } from '@/src/components/SearchResultsFacets/grs.lib';
import {
    BreadcrumbNormalized,
    FacetNormalized,
} from '@/src/components/SearchResultsFacets/types';
import { GRSFacetConfigProperties } from '@/types/contentStack';
import { PageType } from '@/types/pages/search';
import type {
    ContentStackEntryDataProps,
    LocaleProps,
} from '@nxt-costco-com/forge-digital-components';

export function FilterGroup({
    categoryUrlPath,
    facet,
    facetConfig,
    grsFacetConfig,
    locale,
    pageType,
    categoryId,
    breadcrumb,
    searchService,
    publishedCategories = [],
}: {
    categoryUrlPath?: string | undefined;
    facet: FacetNormalized;
    facetConfig: ContentStackEntryDataProps;
    grsFacetConfig?: ContentStackEntryDataProps<GRSFacetConfigProperties>;
    locale: LocaleProps;
    pageType: PageType;
    breadcrumb?: BreadcrumbNormalized;
    searchService: SearchServiceProps;
    categoryId?: string;
    publishedCategories?: string[];
}) {
    const resultsText = getKeyValue('a11y.resultstext', facetConfig);

    // Use boolean cases so dynamic and grouped keys can easily coexist
    switch (true) {
        case facet.facetKey === LW_CATEGORY_FACET_KEY:
            return (
                <CategoryFilterView
                    categoryUrlPath={categoryUrlPath}
                    facet={facet}
                    resultsText={resultsText}
                    facetConfig={facetConfig}
                    pageType={pageType}
                    categoryId={categoryId}
                    breadcrumb={breadcrumb}
                    publishedCategories={publishedCategories}
                />
            );

        case facet.facetKey === GRS_CATEGORY_INFO_FACET_KEY:
            return (
                <CategoryGRSFilterView
                    categoryUrlPath={categoryUrlPath}
                    facet={facet}
                    resultsText={resultsText}
                    facetConfig={facetConfig}
                    categoryId={categoryId}
                    publishedCategories={publishedCategories}
                    grsFacetConfig={grsFacetConfig}
                />
            );

        case [
            LW_DELIVERY_FACET_KEY,
            GRS_DELIVERY_FACET_KEY,
            LW_TWO_DAY_FACET_KEY,
            GRS_TWO_DAY_FACET_KEY,
            LW_BOPIM_FACET_KEY,
            GRS_BOPIM_FACET_KEY,
            LW_BUY_IN_WAREHOUSE_FACET_KEY,
            GRS_BUY_IN_WAREHOUSE_FACET_KEY,
            LW_BOPIW_FACET_KEY,
            GRS_BOPIW_FACET_KEY,
        ].includes(facet.facetKey):
            return (
                <ProgramFilterView
                    facet={facet}
                    facetConfig={facetConfig}
                    resultsText={resultsText}
                    locale={locale}
                    searchService={searchService}
                />
            );

        case [
            LW_SHOW_OUT_OF_STOCK_FACET_KEY,
            GRS_SHOW_OUT_OF_STOCK_FACET_KEY,
        ].includes(facet.facetKey):
            return (
                <LocationFilterView
                    facet={facet}
                    facetConfig={facetConfig}
                    searchService={searchService}
                />
            );

        case LW_PRICE_FACET_KEY === facet.facetKey ||
            isGRSPriceFacetKey(facet.facetKey):
            return (
                <PricingFilterView
                    facet={facet}
                    resultsText={resultsText}
                    facetConfig={facetConfig}
                    searchService={searchService}
                />
            );

        case [LW_RATING_FACET_KEY, GRS_RATING_FACET_KEY].includes(
            facet.facetKey,
        ):
            return (
                <RatingFilterView
                    facetConfig={facetConfig}
                    facet={facet}
                    resultsText={resultsText}
                    searchService={searchService}
                />
            );

        default:
            return (
                <DefaultFilterView
                    facetConfig={facetConfig}
                    facet={facet}
                    resultsText={resultsText}
                    searchService={searchService}
                />
            );
    }
}
